import * as TaskManager from 'expo-task-manager';
import * as BackgroundFetch from 'expo-background-fetch';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {getAccessToken, getUserInfo, setUserInfo} from './storage';
import { generateObjectId } from './objectId';
import { generateHappyId } from './happyId';
import { Platform } from 'react-native';
import ErrorReportingService from './ErrorReportingService';
import {default as BluetoothClassic} from "react-native-bluetooth-classic";

// Define task names
export const FETCH_SYSTEM_DATA_TASK = 'FETCH_SYSTEM_DATA_TASK';
export const FETCH_BOOKINGS_TASK = 'FETCH_BOOKINGS_TASK';
export const STORE_ORDER_TASK = 'STORE_ORDER_TASK';
export const PRINT_RECEIPT_TASK = 'PRINT_RECEIPT_TASK';
export const PRINT_KITCHEN_TASK = 'PRINT_KITCHEN_TASK';
export const FISCAL_PRINTER_TASK = 'FISCAL_PRINTER_TASK';
export const SEND_TO_CASHIER_TASK = 'SEND_TO_CASHIER_TASK';
export const PRINT_CASH_REGISTER_TASK = 'PRINT_CASH_REGISTER_TASK';

// Register background tasks
TaskManager.defineTask(FETCH_SYSTEM_DATA_TASK, async () => {
  try {
    const token = await getAccessToken();
    const restaurantData = await AsyncStorage.getItem('restaurant');
    const restaurantPlaceData = await AsyncStorage.getItem('restaurantPlace');

    if (!token || !restaurantData || !restaurantPlaceData) {
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    const parsedRestaurantData = JSON.parse(restaurantData);
    const parsedRestaurantPlaceData = JSON.parse(restaurantPlaceData);

    // Create an AbortController with a timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    // Declare response variables outside the try block
    let userResponse, foodsResponse, categoriesResponse, vipCardsResponse, bookingsResponse, adminResponse;

    try {
      [userResponse, foodsResponse, categoriesResponse, vipCardsResponse, bookingsResponse, adminResponse] = await Promise.all([
        fetch('https://api.happygastro.hu/api/user/me', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        }),
        fetch(`https://api.happygastro.hu/api/restaurant/${parsedRestaurantData._id}/foods/mobile?placeId=${parsedRestaurantPlaceData._id}&lowKey=true&beta=true&showDisabled=false`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        }),
        fetch(`https://api.happygastro.hu/api/restaurant/${parsedRestaurantData._id}/categories?onlyVisible=true&beta=true`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        }),
        fetch('https://api.happygastro.hu/api/vipcard/list', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        }),
        fetch('https://api.happygastro.hu/api/booking/restaurant', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        }),
        fetch('https://api.happygastro.hu/api/restaurant/admin', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        })
      ]);

      // Clear the timeout since all requests completed
      clearTimeout(timeoutId);
    } catch (error) {
      // Clear the timeout if there was an error
      clearTimeout(timeoutId);

      // Handle AbortError specifically
      if (error.name === 'AbortError') {
        console.error('Fetch requests timed out');
      }

      throw error;
    }

    const userData = await userResponse.json();
    const foodsData = await foodsResponse.json();
    const categoriesData = await categoriesResponse.json();
    const vipCardsData = await vipCardsResponse.json();
    const bookingsData = await bookingsResponse.json();
    const adminData = await adminResponse.json();

    // Filter and store categories with only needed properties
    const filteredCategories = categoriesData
      .filter(category => category.visible && category.enabled && !category.name.hu.toLowerCase().includes('törölt'));
    await AsyncStorage.setItem('categories', JSON.stringify(filteredCategories));

    // Filter and store foods with only needed properties
    const filteredFoods = foodsData
      .filter(food => food.enabled && food.visible);

    const filteredAdmins = adminData.map(admin => ({
      _id: admin._id,
      name: admin.name,
      email: admin.email,
      roles: admin.roles,
      whereIs: admin.whereIs
    }))

    await setUserInfo(userData);
    await AsyncStorage.setItem('lastUser', JSON.stringify(data.user));
    await AsyncStorage.setItem('restaurant', JSON.stringify(data.user?.restaurants[0]));


    await AsyncStorage.setItem('foods', JSON.stringify(filteredFoods));

    await AsyncStorage.setItem('vipCards', JSON.stringify(vipCardsData));
    await AsyncStorage.setItem('bookings', JSON.stringify(bookingsData));
    await AsyncStorage.setItem('admins', JSON.stringify(filteredAdmins));
    // Store other data

    // Set a flag to indicate data has been updated
    await AsyncStorage.setItem('systemDataUpdated', 'true');

    return BackgroundFetch.BackgroundFetchResult.NewData;
  } catch (error) {
    console.error("Background task error:", error);
    return BackgroundFetch.BackgroundFetchResult.Failed;
  }
});

TaskManager.defineTask(FETCH_BOOKINGS_TASK, async () => {
  try {
    const token = await getAccessToken();

    if (!token) {
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    // Create an AbortController with a timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    // Declare response variable
    let response;

    try {
      response = await fetch('https://api.happygastro.hu/api/booking/restaurant', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        signal: controller.signal
      });

      // Clear the timeout since the request completed
      clearTimeout(timeoutId);
    } catch (error) {
      // Clear the timeout if there was an error
      clearTimeout(timeoutId);

      // Handle AbortError specifically
      if (error.name === 'AbortError') {
        console.error('Bookings fetch request timed out');
      }

      throw error;
    }

    const bookingsData = await response.json();
    await AsyncStorage.setItem('bookings', JSON.stringify(bookingsData));
    await AsyncStorage.setItem('bookingsUpdated', 'true');

    return BackgroundFetch.BackgroundFetchResult.NewData;
  } catch (error) {
    console.error("Background bookings task error:", error);
    return BackgroundFetch.BackgroundFetchResult.Failed;
  }
});

// Register background fetch tasks
export const registerBackgroundTasks = async () => {
  try {
    await BackgroundFetch.registerTaskAsync(FETCH_SYSTEM_DATA_TASK, {
      minimumInterval: 15 * 60, // 15 minutes
      stopOnTerminate: false,
      startOnBoot: true,
    });

    await BackgroundFetch.registerTaskAsync(FETCH_BOOKINGS_TASK, {
      minimumInterval: 5 * 60, // 5 minutes
      stopOnTerminate: false,
      startOnBoot: true,
    });

    // Register STORE_ORDER_TASK to ensure it's properly tracked
    await BackgroundFetch.registerTaskAsync(STORE_ORDER_TASK, {
      minimumInterval: 30, // 30 seconds
      stopOnTerminate: false,
      startOnBoot: true,
    });

    console.log('Background tasks registered successfully');
    return true;
  } catch (error) {
    console.error('Background task registration failed:', error);
    return false;
  }
};

// Manually trigger background tasks
export const triggerSystemDataFetch = async () => {
  try {
    await AsyncStorage.setItem('systemDataUpdated', 'false');

    // Instead of using TaskManager.executeTaskAsync which might fail silently,
    // directly execute the task's logic
    const token = await getAccessToken();
    const restaurantData = await AsyncStorage.getItem('restaurant');
    const restaurantPlaceData = await AsyncStorage.getItem('restaurantPlace');

    if (!token || !restaurantData || !restaurantPlaceData) {
      console.log('Missing required data for system data fetch');
      return false;
    }

    const parsedRestaurantData = JSON.parse(restaurantData);
    const parsedRestaurantPlaceData = JSON.parse(restaurantPlaceData);

    console.log('Fetching system data...');

    try {
      // Create an AbortController with a timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      // Declare response variables
      let userResponse, foodsResponse, categoriesResponse, vipCardsResponse, bookingsResponse, adminResponse;

      try {
        [userResponse, foodsResponse, categoriesResponse, vipCardsResponse, bookingsResponse, adminResponse] = await Promise.all([
          fetch('https://api.happygastro.hu/api/user/me', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            signal: controller.signal
          }),
          fetch(`https://api.happygastro.hu/api/restaurant/${parsedRestaurantData._id}/foods/mobile?placeId=${parsedRestaurantPlaceData._id}&lowKey=true&beta=true&showDisabled=false`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            signal: controller.signal
          }),
          fetch(`https://api.happygastro.hu/api/restaurant/${parsedRestaurantData._id}/categories?onlyVisible=true&beta=true`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            signal: controller.signal
          }),
          fetch('https://api.happygastro.hu/api/vipcard/list', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            signal: controller.signal
          }),
          fetch('https://api.happygastro.hu/api/booking/restaurant', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            signal: controller.signal
          }),
          fetch('https://api.happygastro.hu/api/restaurant/admin', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            signal: controller.signal
          }),
        ]);

        // Clear the timeout since all requests completed
        clearTimeout(timeoutId);
      } catch (error) {
        // Clear the timeout if there was an error
        clearTimeout(timeoutId);

        // Handle AbortError specifically
        if (error.name === 'AbortError') {
          console.error('System data fetch requests timed out');
          return false;
        }

        throw error;
      }

      const userData = await userResponse.json();
      const foodsData = await foodsResponse.json();
      const categoriesData = await categoriesResponse.json();
      const vipCardsData = await vipCardsResponse.json();
      const bookingsData = await bookingsResponse.json();
      const adminData = await adminResponse.json();

      // Filter and store categories with only needed properties
      const filteredCategories = categoriesData
        .filter(category => category.visible && category.enabled && !category.name.hu.toLowerCase().includes('törölt'));
      await AsyncStorage.setItem('categories', JSON.stringify(filteredCategories));

      await AsyncStorage.setItem('foods', JSON.stringify(foodsData));

      const filteredAdmins = adminData.map(admin => ({
        _id: admin._id,
        name: admin.name,
        email: admin.email,
        roles: admin.roles,
        whereIs: admin.whereIs
      }))
      // Store other data
      await setUserInfo(userData);
      await AsyncStorage.setItem('user', JSON.stringify(userData));
      await AsyncStorage.setItem('vipCards', JSON.stringify(vipCardsData));
      await AsyncStorage.setItem('bookings', JSON.stringify(bookingsData));
      await AsyncStorage.setItem('admins', JSON.stringify(filteredAdmins));

      // Set a flag to indicate data has been updated
      await AsyncStorage.setItem('systemDataUpdated', 'true');

      console.log('System data fetched and stored successfully');
      return true;
    } catch (fetchError) {
      console.error('Error fetching system data:', fetchError);
      return false;
    }
  } catch (error) {
    console.error('Failed to trigger system data fetch:', error);
    return false;
  }
};

export const triggerBookingsFetch = async () => {
  try {
    await AsyncStorage.setItem('bookingsUpdated', 'false');

    // Use TaskManager.executeTaskAsync on Android, direct implementation on web and other platforms
    if (Platform.OS === 'android') {
      console.log('Using TaskManager for bookings fetch on Android');
      try {
        await TaskManager.executeTaskAsync(FETCH_BOOKINGS_TASK);
        return true;
      } catch (error) {
        console.error('Failed to trigger bookings fetch with TaskManager:', error);
        // Fall back to direct implementation if TaskManager fails
        console.log('Falling back to direct implementation');
      }
    }

    // Direct implementation for web and other platforms, or as fallback for Android
    console.log(`Fetching bookings data directly on ${Platform.OS}...`);
    const token = await getAccessToken();

    if (!token) {
      console.log('Missing required data for bookings fetch');
      return false;
    }

    try {
      // Create an AbortController with a timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      let response;

      try {
        response = await fetch('https://api.happygastro.hu/api/booking/restaurant', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        });

        // Clear the timeout since the request completed
        clearTimeout(timeoutId);
      } catch (error) {
        // Clear the timeout if there was an error
        clearTimeout(timeoutId);

        // Handle AbortError specifically
        if (error.name === 'AbortError') {
          console.error('Direct bookings fetch request timed out');
          return false;
        }

        throw error;
      }

      const bookingsData = await response.json();
      await AsyncStorage.setItem('bookings', JSON.stringify(bookingsData));
      await AsyncStorage.setItem('bookingsUpdated', 'true');

      console.log('Bookings data fetched and stored successfully');
      return true;
    } catch (fetchError) {
      console.error('Error fetching bookings data:', fetchError);
      return false;
    }
  } catch (error) {
    console.error('Failed to trigger bookings fetch:', error);
    return false;
  }
};

// Check if data has been updated
export const checkSystemDataUpdated = async () => {
  const updated = await AsyncStorage.getItem('systemDataUpdated');
  return updated === 'true';
};

export const checkBookingsUpdated = async () => {
  const updated = await AsyncStorage.getItem('bookingsUpdated');
  return updated === 'true';
};

// Define store order task
TaskManager.defineTask(STORE_ORDER_TASK, async ({ data }) => {
  try {
    // Check if this order has already been processed recently
    const lastOrderResponse = await AsyncStorage.getItem('lastOrderResponse');
    const lastProcessedTime = await AsyncStorage.getItem('lastOrderProcessedTime');

    if (lastOrderResponse && lastProcessedTime && data.orderId) {
      const lastOrder = JSON.parse(lastOrderResponse);
      const currentTime = new Date().getTime();
      const lastTime = parseInt(lastProcessedTime);

      // If the same order was processed in the last 5 minutes, don't process it again
      if (lastOrder &&
          lastOrder.generatedId === data.orderId &&
          (currentTime - lastTime) < 5 * 60 * 1000) {
        console.log('Order already processed recently by background task, skipping duplicate submission');
        return BackgroundFetch.BackgroundFetchResult.NoData;
      }
    }

    // Validate that the order has items
    const cartItems = data.cartItems || [];
    //const alreadySentCartItems = data.alreadySentCartItems || [];

    if (cartItems.length === 0 && alreadySentCartItems.length === 0) {
      console.error('Empty order detected, skipping database storage');
      ErrorReportingService.info("Empty order detected, skipping database storage",
        { orderId: data.orderId, orderCode: data.orderCode },
        "STORE_ORDER_TASK");
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    // Check if all items have zero price
    const hasNonZeroPrice = [...cartItems, ...alreadySentCartItems].some(item =>
      (item.remotePrice || item.price || 0) > 0
    );

    if (!hasNonZeroPrice) {
      console.error('Order with zero price detected, skipping database storage');
      ErrorReportingService.info("Order with zero price detected, skipping database storage",
        { orderId: data.orderId, orderCode: data.orderCode, itemCount: cartItems.length + alreadySentCartItems.length },
        "STORE_ORDER_TASK");
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    // Set current time as the processing time for this order
    await AsyncStorage.setItem('lastOrderProcessedTime', new Date().getTime().toString());


    const {
      cartTotal,
      paymentType,
      paidAmount,
      discountsForWholeCart,
      vatInvoiceData,
      needInvoice,
      currentTable,
      serviceFeeAmount,
      isSavedForLater,
      alreadySentCartItems,
      orderId,
      paymentTypes,
      tip,
      originalAmount,
      selectedVipCard,
      userData,
      selectedItemsToPay,
      order,
      proform
    } = data;

    const token = await getAccessToken();
    const restaurantData = await AsyncStorage.getItem("restaurant");
    const restaurantPlaceData = await AsyncStorage.getItem("restaurantPlace");

    if (!token || !restaurantData || !restaurantPlaceData) {
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    const restaurant = JSON.parse(restaurantData);
    const restaurantPlace = JSON.parse(restaurantPlaceData);

    const API_URL = 'https://api.happygastro.hu/api';
    const orderCode = generateHappyId();

    const convertPaymentType = () => {
      if (paymentType === "Készpénz") return "cash";
      if (paymentType === "Bankkártya") return "creditCard";
      if (paymentType === "Utalvány") return "voucher";
      if (paymentType === "VIP") return "vipBalance";
      if (paymentType === "Személyzeti") return "employeeBalance";
      return "temporary";
    };

    const itemsArrayforOrder = [
      ...(cartItems ?? []).map(item => {
        return {
          "item": item.id,
          "sizeIndex": item.sizes[item?.sizeIndex || 0]._id,
          "size": item.sizes[item?.sizeIndex || 0]._id,
          "quantity": (item.amount || item.quantity || 1),
          "extras": item.extras || [],
          "serveType": item.serveType || "",
          "comments": item.comments || [],
          "discounts": item.discounts || {},
          "round": item.round || 0,
          "amount": (item.amount || item.quantity || 1),
          "price": item.price,
          "remotePrice": item.remotePrice || item.price,
        };
      }),
      ...(alreadySentCartItems ?? []).map(item => {
        return {
          "item": item.item._id,
          "sizeIndex": item.size,
          "size": item.size,
          "quantity": (item.amount || item.quantity || 0),
          "extras": item.extras || [],
          "serveType": item.serveType || "",
          "comments": item.comments || [],
          "discounts": item.discounts || {},
          "round": item.round || 0,
          "amount": (item.amount || item.quantity || 0),
          "price": item.price,
          "remotePrice": item.remotePrice || item.price,
        };
      })
    ];

    const invoiceData = vatInvoiceData ? {
      "company": {
        "addressData": {
          "fullAddress": vatInvoiceData?.fullAddress || "",
          "city": vatInvoiceData?.city || "",
          "postCode": vatInvoiceData?.postCode || "",
          "street": vatInvoiceData?.address || "",
          "streetType": vatInvoiceData?.streetType || "",
          "houseNumber": vatInvoiceData?.houseNumber || "",
        },
        "nev": vatInvoiceData?.name || "",
        "adoszam": vatInvoiceData?.taxRefNum || "",
        "cim": vatInvoiceData?.fullAddress || "",
        "cegj_sz": vatInvoiceData?.cegj_sz || "",
        "arbev": vatInvoiceData?.arbev || "",
      },
      "firstName": vatInvoiceData?.firstName || "",
      "lastName": vatInvoiceData?.lastName || "",
      "email": vatInvoiceData?.email || "",
      "phone": vatInvoiceData?.phone || "",
      "comment": vatInvoiceData?.comment || "",
    } : null;

    const orderType = (isSavedForLater || selectedItemsToPay?.length > 0 || currentTable ? "dineIn" : (userData?.orderType ? userData.orderType : "takeAway"));
    const currentPaymentType = convertPaymentType();
    const bodyPaymentTypes = paymentTypes && paymentTypes.length > 0 ? paymentTypes : [
      {
        "paymentType": convertPaymentType(),
        "amount": paidAmount || cartTotal || 0,
        "change": 1,
        "currency": "HUF",
      }
    ];
    const selectedItems = selectedItemsToPay ? ({"selectedItems": selectedItemsToPay}) : {};

    const bodyData = {
      "VIPCard": userData ? null : (selectedVipCard ? selectedVipCard?._id : null),
      "generatedId": (orderType === "takeAway" ? generateObjectId() : (orderId ? orderId : generateObjectId())),
      "orderCode": (orderType === "takeAway" ? generateHappyId() : (order ? order?.orderCode : generateHappyId())),
      "restaurant": restaurant._id,
      "addressInfo": userData?.addressInfo || {},
      "userInfo": userData?.userInfo || {},
      "placeId": restaurantPlace._id,
      "preOrder": userData?.preOrder ? userData?.preOrder : null,
      "orderType": orderType,
      "serviceFee": currentTable?._id && restaurantPlace?.serviceFee || 0,
      "deliveryFee": 0,
      "editOrderSum": alreadySentCartItems && alreadySentCartItems.length > 0 ? alreadySentCartItems?.reduce((sum, item) => sum + (item.remotePrice || item.price) * (item.amount || item.quantity || 1), 0) : undefined,
      "serviceFeePrice": currentTable?._id && serviceFeeAmount || 0,
      "serviceFeeName": "",
      "orderFrom": restaurantPlace._id,
      "selectedPortal": userData?.selectedPortal || restaurantPlace._id,
      "proforma": proform || false,
      "tableId": currentTable?._id || "",
      "tableName": currentTable?.name || "",
      "tipAmount": tip || 0,
      "comment": "",
      ...selectedItems,
      "discounts": discountsForWholeCart,
      "paymentType": paymentTypes && paymentTypes.length > 0 ? paymentTypes[0].paymentType : currentPaymentType,
      "paymentTypes": isSavedForLater ? [] : bodyPaymentTypes,
      "items": [...itemsArrayforOrder],
      "originalItems": [...itemsArrayforOrder],
      "cartItems": [],
      "needInvoice": needInvoice || false,
    };

    if (isSavedForLater) {
      bodyData["isPayed"] = false;
    }

    const requestBody = invoiceData
      ? { ...bodyData, invoiceData }
      : bodyData;

    // Create an AbortController with a timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    // Declare response variable outside the try block
    let response;

    try {
      response = await fetch(`${API_URL}/order/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      // Clear the timeout since the request completed
      clearTimeout(timeoutId);
    } catch (error) {
      // Clear the timeout if there was an error
      clearTimeout(timeoutId);

      // Handle AbortError specifically
      if (error.name === 'AbortError') {
        console.error('Order creation request timed out');
      }

      throw error;
    }

    const responseData = await response.json();

    // Store the order response
    await AsyncStorage.setItem('lastOrderResponse', JSON.stringify(responseData));
    await AsyncStorage.setItem('orderStored', 'true');

    return BackgroundFetch.BackgroundFetchResult.NewData;
  } catch (error) {
    console.error("Background store order task error:", error);
    return BackgroundFetch.BackgroundFetchResult.Failed;
  }
});

// Trigger store order task
export const triggerStoreOrder = async (orderData) => {
  try {
    // Check if this order has already been processed recently
    const lastOrderResponse = await AsyncStorage.getItem('lastOrderResponse');
    const lastProcessedTime = await AsyncStorage.getItem('lastOrderProcessedTime');

    if (lastOrderResponse && lastProcessedTime) {
      const lastOrder = JSON.parse(lastOrderResponse);
      const currentTime = new Date().getTime();
      const lastTime = parseInt(lastProcessedTime);

      // If the same order was processed in the last 5 minutes, don't process it again
      if (lastOrder &&
          orderData.orderId &&
          lastOrder.generatedId === orderData.orderId &&
          (currentTime - lastTime) < 5 * 60 * 1000) {
        console.log('Order already processed recently, skipping duplicate submission');
        return true;
      }
    }

    // Set current time as the processing time for this order
    await AsyncStorage.setItem('lastOrderProcessedTime', new Date().getTime().toString());
    await AsyncStorage.setItem('orderStored', 'false');

    // Instead of using TaskManager.executeTaskAsync which might fail silently,
    // directly execute the task's logic
    const {
      cartTotal,
      cartItems,
      paymentType,
      paidAmount,
      discountsForWholeCart,
      vatInvoiceData,
      needInvoice,
      currentTable,
      serviceFeeAmount,
      isSavedForLater,
      alreadySentCartItems,
      orderId,
      paymentTypes,
      tip,
      originalAmount,
      selectedVipCard,
      userData,
      selectedItemsToPay,
      order,
      proform
    } = orderData;

    const token = await getAccessToken();
    const restaurantData = await AsyncStorage.getItem("restaurant");
    const restaurantPlaceData = await AsyncStorage.getItem("restaurantPlace");

    if (!token || !restaurantData || !restaurantPlaceData) {
      console.log('Missing required data for store order');
      return false;
    }

    const restaurant = JSON.parse(restaurantData);
    const restaurantPlace = JSON.parse(restaurantPlaceData);

    const API_URL = 'https://api.happygastro.hu/api';
    const orderCode = generateHappyId();

    const convertPaymentType = () => {
      if (paymentType === "Készpénz") return "cash";
      if (paymentType === "Bankkártya") return "creditCard";
      if (paymentType === "Utalvány") return "voucher";
      if (paymentType === "VIP") return "vipBalance";
      if (paymentType === "Személyzeti") return "employeeBalance";
      return "temporary";
    };
    const itemsArrayforOrder = [
      ...(cartItems ?? []).map(item => {

        return {
          "item": item.itemId,
          "sizeIndex": item?.sizeIndex || 0,
          "size": item.sizes[item?.sizeIndex || 0]._id,
          "quantity": (item.amount || item.quantity || 1),
          "extras": item.extras || [],
          "serveType": item.serveType || "",
          "comments": item.comments || [],
          "discounts": item.discounts || {},
          "round": item.round || 0,
          "amount": (item.amount || item.quantity || 1),
          "price": item.price,
          "remotePrice": item.remotePrice || item.price,
        };
      }),
      ...(alreadySentCartItems ?? []).map(item => {
        return {
          "item": item.item._id,
          "sizeIndex": item.size,
          "size": item.size,
          "quantity": (item.amount || item.quantity || 0),
          "extras": item.extras || [],
          "serveType": item.serveType || "",
          "comments": item.comments || [],
          "discounts": item.discounts || {},
          "round": item.round || 0,
          "amount": (item.amount || item.quantity || 0),
          "price": item.price,
          "remotePrice": item.remotePrice || item.price,
        };
      })
    ];

    const invoiceData = vatInvoiceData ? {
      "company": {
        "addressData": {
          "fullAddress": vatInvoiceData?.fullAddress || "",
          "city": vatInvoiceData?.city || "",
          "postCode": vatInvoiceData?.postCode || "",
          "street": vatInvoiceData?.address || "",
          "streetType": vatInvoiceData?.streetType || "",
          "houseNumber": vatInvoiceData?.houseNumber || "",
        },
        "nev": vatInvoiceData?.name || "",
        "adoszam": vatInvoiceData?.taxRefNum || "",
        "cim": vatInvoiceData?.fullAddress || "",
        "cegj_sz": vatInvoiceData?.cegj_sz || "",
        "arbev": vatInvoiceData?.arbev || "",
      },
      "firstName": vatInvoiceData?.firstName || "",
      "lastName": vatInvoiceData?.lastName || "",
      "email": vatInvoiceData?.email || "",
      "phone": vatInvoiceData?.phone || "",
      "comment": vatInvoiceData?.comment || "",
    } : null;

    const orderType = (isSavedForLater || selectedItemsToPay?.length > 0 || currentTable ? "dineIn" : (userData?.orderType ? userData.orderType : "takeAway"));
    const currentPaymentType = convertPaymentType();
    const bodyPaymentTypes = paymentTypes && paymentTypes.length > 0 ? paymentTypes : [
      {
        "paymentType": convertPaymentType(),
        "amount": paidAmount || cartTotal || 0,
        "change": 1,
        "currency": "HUF",
      }
    ];
    const selectedItems = selectedItemsToPay ? ({"selectedItems": selectedItemsToPay}) : {};

    const bodyData = {
      "VIPCard": userData ? null : (selectedVipCard ? selectedVipCard?._id : null),
      "generatedId": (orderType === "takeAway" ? generateObjectId() : (orderId ? orderId : generateObjectId())),
      "orderCode": (orderType === "takeAway" ? generateHappyId() : (order ? order?.orderCode : generateHappyId())),
      "restaurant": restaurant._id,
      "addressInfo": userData?.addressInfo || {},
      "userInfo": userData?.userInfo || {},
      "placeId": restaurantPlace._id,
      "preOrder": userData?.preOrder ? userData?.preOrder : null,
      "orderType": orderType,
      "serviceFee": currentTable?._id && restaurantPlace?.serviceFee || 0,
      "deliveryFee": 0,
      "editOrderSum": alreadySentCartItems && alreadySentCartItems.length > 0 ? alreadySentCartItems?.reduce((sum, item) => sum + (item.remotePrice || item.price) * (item.amount || item.quantity || 1), 0) : undefined,
      "serviceFeePrice": currentTable?._id && serviceFeeAmount || 0,
      "serviceFeeName": "",
      "orderFrom": restaurantPlace._id,
      "selectedPortal": userData?.selectedPortal || restaurantPlace._id,
      "proforma": proform || false,
      "tableId": currentTable?._id || "",
      "tableName": currentTable?.name || "",
      "tipAmount": tip || 0,
      "comment": "",
      ...selectedItems,
      "discounts": discountsForWholeCart,
      "paymentType": paymentTypes && paymentTypes.length > 0 ? paymentTypes[0].paymentType : currentPaymentType,
      "paymentTypes": isSavedForLater ? [] : bodyPaymentTypes,
      "items": [...itemsArrayforOrder],
      "originalItems": [...itemsArrayforOrder],
      "cartItems": [],
      "needInvoice": needInvoice || false,
    };

    if (isSavedForLater) {
      bodyData["isPayed"] = false;
    }

    const requestBody = invoiceData
      ? { ...bodyData, invoiceData }
      : bodyData;

    console.log('Sending order to API...');

    try {
      const response = await fetch(`${API_URL}/order/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      // Store the order response
      await AsyncStorage.setItem('lastOrderResponse', JSON.stringify(responseData));
      await AsyncStorage.setItem('orderStored', 'true');

      console.log('Order stored successfully');
      return true;
    } catch (apiError) {
      console.error('API error when storing order:', apiError);
      return false;
    }
  } catch (error) {
    console.error('Failed to trigger store order:', error);
    return false;
  }
};

// Check if order has been stored
export const checkOrderStored = async () => {
  const stored = await AsyncStorage.getItem('orderStored');
  return stored === 'true';
};

// Get the last order response
export const getLastOrderResponse = async () => {
  const response = await AsyncStorage.getItem('lastOrderResponse');
  return response ? JSON.parse(response) : null;
};

// Define receipt printing task
TaskManager.defineTask(PRINT_RECEIPT_TASK, async ({ data }) => {
  try {
    const { order } = data;

    if (!order) {
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    // Import Toast for notifications
    const Toast = require('react-native-toast-message').default;

    // Show printing in progress notification
    Toast.show({
      type: 'info',
      position: 'bottom',
      text1: 'Printing Receipt...',
      visibilityTime: 10000, // 10 seconds or until dismissed
    });

    const userInfo = await getUserInfo();
    const printerDevice = await AsyncStorage.getItem('printerDevice');
    const restaurantData = await AsyncStorage.getItem("restaurant");
    const restaurantPlaceData = await AsyncStorage.getItem("restaurantPlace");
    let printerData = printerDevice ? JSON.parse(printerDevice) : null;

    // Import required functions
    const { sendPrinterAndroid } = require('./printerLibrary/printer');
    const { getBigReceiptData } = require('./printerLibrary/oldPrinter');

    // Print to default printer if available
    if (printerData && (printerData.address || printerData.macAddress)) {
      await sendPrinterAndroid(
        getBigReceiptData(
          order,
          JSON.parse(restaurantData),
          JSON.parse(restaurantPlaceData),
          userInfo
        ),
        {
          ...printerData,
          macAddress: (printerData.address || printerData.macAddress)
        }
      );
    }

    // Print to receipt printers
    const storedPrinterEvents = await AsyncStorage.getItem('printerEvents');
    const printerEvents = storedPrinterEvents ? JSON.parse(storedPrinterEvents) : [];

    if (printerEvents.length > 0) {
      const event = printerEvents.find(event => event.type === "receipt");
      if (event && event.printers && event.printers.length > 0) {
        for (let i = 0; i < event.printers.length; i++) {
          const printer = event.printers[i];
          if (printer.ip) {
            await sendPrinterAndroid(
              getBigReceiptData(
                order,
                JSON.parse(restaurantData),
                JSON.parse(restaurantPlaceData),
                userInfo
              ),
              {
                ip: printer.ip,
                port: Number(printer.port),
                timeout: 400,
                autoCut: true,
                mmFeedPaper: 230,
                printerDpi: 203,
                printerWidthMM: 76,
                printerNbrCharactersPerLine: 48,
              }
            );
          } else if (printer.address || printer.macAddress) {
            await sendPrinterAndroid(
              getBigReceiptData(
                order,
                JSON.parse(restaurantData),
                JSON.parse(restaurantPlaceData),
                userInfo
              ),
              {
                macAddress: (printer.address || printer.macAddress)
              }
            );
          }
          // Add a small delay between prints
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    }

    await AsyncStorage.setItem('receiptPrinted', 'true');

    // Hide the notification when printing is complete
    Toast.hide();

    return BackgroundFetch.BackgroundFetchResult.NewData;
  } catch (error) {
    console.error("Background receipt printing task error:", error);

    // Hide the notification and show error if printing fails
    Toast.hide();
    Toast.show({
      type: 'error',
      position: 'bottom',
      text1: 'Receipt printing failed',
      text2: error.message,
      visibilityTime: 3000,
    });

    return BackgroundFetch.BackgroundFetchResult.Failed;
  }
});

// Trigger receipt printing task
export const triggerReceiptPrinting = async (order) => {
  try {
    await AsyncStorage.setItem('receiptPrinted', 'false');

    // Execute the task directly
    const { Platform } = require('react-native');
    if (Platform.OS !== 'android') return false;

    const { sendPrinterAndroid, showPrintErrorDialog } = require('./printerLibrary/printer');
    const { getBigReceiptData } = require('./printerLibrary/oldPrinter');

    try {
      // Execute the task with the data
      await TaskManager.executeTaskAsync(PRINT_RECEIPT_TASK, { order });
      return true;
    } catch (taskError) {
      console.error('Failed to execute receipt printing task:', taskError);

      // Fall back to direct implementation
      const userInfo = await getUserInfo();
      const printerDevice = await AsyncStorage.getItem('printerDevice');
      const restaurantData = await AsyncStorage.getItem("restaurant");
      const restaurantPlaceData = await AsyncStorage.getItem("restaurantPlace");
      let printerData = printerDevice ? JSON.parse(printerDevice) : null;

      if (printerData && (printerData.address || printerData.macAddress)) {
        await sendPrinterAndroid(
          getBigReceiptData(
            order,
            JSON.parse(restaurantData),
            JSON.parse(restaurantPlaceData),
            userInfo
          ),
          {
            ...printerData,
            macAddress: (printerData.address || printerData.macAddress)
          }
        );
      }

      const storedPrinterEvents = await AsyncStorage.getItem('printerEvents');
      const printerEvents = storedPrinterEvents ? JSON.parse(storedPrinterEvents) : [];

      if (printerEvents.length > 0) {
        const event = printerEvents.find(event => event.type === "receipt");
        if (event && event.printers && event.printers.length > 0) {
          for (let i = 0; i < event.printers.length; i++) {
            const printer = event.printers[i];
            if (printer.ip) {
              await sendPrinterAndroid(
                getBigReceiptData(
                  order,
                  JSON.parse(restaurantData),
                  JSON.parse(restaurantPlaceData),
                  userInfo
                ),
                {
                  ip: printer.ip,
                  port: Number(printer.port),
                  timeout: 400,
                  autoCut: true,
                  mmFeedPaper: 230,
                  printerDpi: 203,
                  printerWidthMM: 76,
                  printerNbrCharactersPerLine: 48,
                }
              );
            } else if (printer.address || printer.macAddress) {
              await sendPrinterAndroid(
                getBigReceiptData(
                  order,
                  JSON.parse(restaurantData),
                  JSON.parse(restaurantPlaceData),
                  userInfo
                ),
                {
                  macAddress: (printer.address || printer.macAddress)
                }
              );
            }
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      }

      await AsyncStorage.setItem('receiptPrinted', 'true');
      return true;
    }
  } catch (error) {
    console.error('Failed to trigger receipt printing:', error);
    return false;
  }
};

// Check if receipt has been printed
export const checkReceiptPrinted = async () => {
  const printed = await AsyncStorage.getItem('receiptPrinted');
  return printed === 'true';
};

// Trigger send to cashier task
export const triggerSendToCashier = async (qrCodeString) => {
  if (!qrCodeString) {
    console.error('No QR code string provided for cashier');
    return false;
  }


  // Fall back to direct implementation
  const { Platform } = require('react-native');
  if (Platform.OS !== 'android') return false;

  const BluetoothClassic = require('react-native-bluetooth-classic').default;
  const AsyncStorage = require('@react-native-async-storage/async-storage').default;

  try {
    const storedDevice = await AsyncStorage.getItem('cashierDevice');
    const storedDeviceData = JSON.parse(storedDevice) || storedDevice;

    if (!storedDeviceData || !storedDeviceData.address) {
      return false;
    }

    const deviceAddress = storedDeviceData.address;

    const connection = await BluetoothClassic.connectToDevice(deviceAddress);
    await BluetoothClassic.writeToDevice(deviceAddress, qrCodeString);
    return true;
  } catch (error) {
    console.error('Failed to send data to cashier directly:', error);
    return { error, qrCodeString };
  }
};

// Trigger cash register receipt printing task
export const triggerPrintCashRegister = async (order) => {
  if (!order) {
    console.error('No order provided for cash register receipt');
    return false;
  }

  try {
    // Execute the task with the data
    await TaskManager.executeTaskAsync(PRINT_CASH_REGISTER_TASK, { order });
    return true;
  } catch (taskError) {
    console.error('Failed to execute cash register printing task:', taskError);

    // Fall back to direct implementation
    const { Platform } = require('react-native');
    if (Platform.OS !== 'android') return false;

    const { sendPrinterAndroid, showPrintErrorDialog } = require('./printerLibrary/printer');
    const { getCashRegisterReceiptData } = require('./printerLibrary/oldPrinter');

    try {
      const userInfo = await getUserInfo();
      const printerDevice = await AsyncStorage.getItem('printerDevice');
      const restaurantData = await AsyncStorage.getItem("restaurant");
      const restaurantPlaceData = await AsyncStorage.getItem("restaurantPlace");
      let printerData = printerDevice ? JSON.parse(printerDevice) : null;

      if (printerData && (printerData.address || printerData.macAddress)) {
        await sendPrinterAndroid(
          getCashRegisterReceiptData(
            order,
            JSON.parse(restaurantData),
            JSON.parse(restaurantPlaceData),
            userInfo
          ),
          {
            ...printerData,
            macAddress: (printerData.address || printerData.macAddress)
          }
        );
      }

      const storedPrinterEvents = await AsyncStorage.getItem('printerEvents');
      const printerEvents = storedPrinterEvents ? JSON.parse(storedPrinterEvents) : [];

      if (printerEvents.length > 0) {
        const event = printerEvents.find(event => event.type === "cashRegister");
        if (event && event.printers && event.printers.length > 0) {
          for (let i = 0; i < event.printers.length; i++) {
            const printer = event.printers[i];
            if (printer.ip) {
              await sendPrinterAndroid(
                getCashRegisterReceiptData(
                  order,
                  JSON.parse(restaurantData),
                  JSON.parse(restaurantPlaceData),
                  userInfo
                ),
                {
                  ip: printer.ip,
                  port: Number(printer.port),
                  timeout: 400,
                  autoCut: true,
                  mmFeedPaper: 230,
                  printerDpi: 203,
                  printerWidthMM: 76,
                  printerNbrCharactersPerLine: 48,
                }
              );
            } else if (printer.address || printer.macAddress) {
              await sendPrinterAndroid(
                getCashRegisterReceiptData(
                  order,
                  JSON.parse(restaurantData),
                  JSON.parse(restaurantPlaceData),
                  userInfo
                ),
                {
                  macAddress: (printer.address || printer.macAddress)
                }
              );
            }
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      }

      return true;
    } catch (error) {
      console.error('Failed to print cash register receipt directly:', error);
      return { error, order };
    }
  }
};

// Define send to cashier task
TaskManager.defineTask(SEND_TO_CASHIER_TASK, async ({ data }) => {
  try {
    const { qrCodeString } = data;

    if (!qrCodeString) {
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    const { Platform } = require('react-native');
    if (Platform.OS !== 'android') return BackgroundFetch.BackgroundFetchResult.NoData;

    const BluetoothClassic = require('react-native-bluetooth-classic').default;
    const AsyncStorage = require('@react-native-async-storage/async-storage').default;
    const Toast = require('react-native-toast-message').default;

    try {
      const storedDevice = await AsyncStorage.getItem('cashierDevice');
      const storedDeviceData = JSON.parse(storedDevice) || storedDevice;

      if (!storedDeviceData || !storedDeviceData.address) {
        return BackgroundFetch.BackgroundFetchResult.Failed;
      }

      const deviceAddress = storedDeviceData.address;

      try {
        const connection = await BluetoothClassic.connectToDevice(deviceAddress);
        await BluetoothClassic.writeToDevice(deviceAddress, qrCodeString);
        return BackgroundFetch.BackgroundFetchResult.NewData;
      } catch (error) {
        ErrorReportingService.error('Failed to send data to cashier', { error }, 'SEND_TO_CASHIER_TASK');
        ErrorReportingService.reportError(error, { source: 'SEND_TO_CASHIER_TASK', deviceAddress });
        return BackgroundFetch.BackgroundFetchResult.Failed;
      }
    } catch (error) {
      ErrorReportingService.error('Failed to get cashier device', { error }, 'SEND_TO_CASHIER_TASK');
      ErrorReportingService.reportError(error, { source: 'SEND_TO_CASHIER_TASK' });
      return BackgroundFetch.BackgroundFetchResult.Failed;
    }
  } catch (error) {
    ErrorReportingService.error('Error in SEND_TO_CASHIER_TASK', { error }, 'SEND_TO_CASHIER_TASK');
    ErrorReportingService.reportError(error, { source: 'SEND_TO_CASHIER_TASK', task: 'main' });
    return BackgroundFetch.BackgroundFetchResult.Failed;
  }
});

// Define cash register receipt task
TaskManager.defineTask(PRINT_CASH_REGISTER_TASK, async ({ data }) => {
  try {
    const { order } = data;

    if (!order) {
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    const { Platform } = require('react-native');
    if (Platform.OS !== 'android') return BackgroundFetch.BackgroundFetchResult.NoData;

    const { sendPrinterAndroid } = require('./printerLibrary/printer');
    const { getCashRegisterReceiptData } = require('./printerLibrary/oldPrinter');
    const AsyncStorage = require('@react-native-async-storage/async-storage').default;
    const { getUserInfo } = require('./storage');

    try {
      const userInfo = await getUserInfo();
      const printerDevice = await AsyncStorage.getItem('printerDevice');
      const restaurantData = await AsyncStorage.getItem("restaurant");
      const restaurantPlaceData = await AsyncStorage.getItem("restaurantPlace");
      let printerData = printerDevice ? JSON.parse(printerDevice) : null;

      if (printerData && (printerData.address || printerData.macAddress)) {
        await sendPrinterAndroid(
          getCashRegisterReceiptData(
            order,
            JSON.parse(restaurantData),
            JSON.parse(restaurantPlaceData),
            userInfo
          ),
          {
            ...printerData,
            macAddress: (printerData.address || printerData.macAddress)
          },
          true,
          false
        );
      }

      const storedPrinterEvents = await AsyncStorage.getItem('printerEvents');
      const printerEvents = storedPrinterEvents ? JSON.parse(storedPrinterEvents) : [];

      if (printerEvents.length > 0) {
        const event = printerEvents.find(event => event.type === "cashRegister");
        if (event && event.printers && event.printers.length > 0) {
          for (let i = 0; i < event.printers.length; i++) {
            const printer = event.printers[i];
            if (printer.ip) {
              await sendPrinterAndroid(
                getCashRegisterReceiptData(
                  order,
                  JSON.parse(restaurantData),
                  JSON.parse(restaurantPlaceData),
                  userInfo
                ),
                {
                  ip: printer.ip,
                  port: Number(printer.port),
                  timeout: 400,
                  autoCut: true,
                  mmFeedPaper: 230,
                  printerDpi: 203,
                  printerWidthMM: 76,
                  printerNbrCharactersPerLine: 48,
                },
                true,
                false
              );
            } else if (printer.address || printer.macAddress) {
              await sendPrinterAndroid(
                getCashRegisterReceiptData(
                  order,
                  JSON.parse(restaurantData),
                  JSON.parse(restaurantPlaceData),
                  userInfo
                ),
                {
                  macAddress: (printer.address || printer.macAddress)
                },
                true,
                false
              );
            }
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      }

      return BackgroundFetch.BackgroundFetchResult.NewData;
    } catch (error) {
      ErrorReportingService.error('Failed to print cash register receipt', { error }, 'PRINT_CASH_REGISTER_TASK');
      ErrorReportingService.reportError(error, { source: 'PRINT_CASH_REGISTER_TASK', order: order?.orderCode });
      return BackgroundFetch.BackgroundFetchResult.Failed;
    }
  } catch (error) {
    ErrorReportingService.error('Error in PRINT_CASH_REGISTER_TASK', { error }, 'PRINT_CASH_REGISTER_TASK');
    ErrorReportingService.reportError(error, { source: 'PRINT_CASH_REGISTER_TASK', task: 'main' });
    return BackgroundFetch.BackgroundFetchResult.Failed;
  }
});

// Define kitchen printer task
TaskManager.defineTask(PRINT_KITCHEN_TASK, async ({ data }) => {
  try {
    const { order, items } = data;

    if (!order || !items) {
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    // Import Toast for notifications
    const Toast = require('react-native-toast-message').default;

    // Show printing in progress notification
    Toast.show({
      type: 'info',
      position: 'bottom',
      text1: 'Printing Kitchen...',
      visibilityTime: 10000, // 10 seconds or until dismissed
    });

    const { Platform } = require('react-native');
    if (Platform.OS !== 'android') {
      // Hide the notification if not on Android
      Toast.hide();
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    const userInfo = await getUserInfo();
    const restaurantData = await AsyncStorage.getItem("restaurant");

    // Import required functions
    const { sendPrinterAndroid } = require('./printerLibrary/printer');
    const { getBigTerminalData } = require('./printerLibrary/oldPrinter');

    // Print to kitchen printers
    const storedPrinterEvents = await AsyncStorage.getItem('printerEvents');
    const printerEvents = storedPrinterEvents ? JSON.parse(storedPrinterEvents) : [];

    if (printerEvents.length > 0) {
      const terminalEvents = printerEvents.filter(event => event.type === "terminal");

      for (let eventIndex = 0; eventIndex < terminalEvents.length; eventIndex++) {
        const event = terminalEvents[eventIndex];

        if (event.printers && event.printers.length > 0) {
          for (let printerIndex = 0; printerIndex < event.printers.length; printerIndex++) {
            const printer = event.printers[printerIndex];

            // Filter items based on event.products
            const filteredItems = items.filter(item =>
              event?.products?.includes(item.itemId || item.item._id || item.item)
            );

            if (!filteredItems || filteredItems.length === 0) {
              console.log("No items for this printer event", {name: event.name, products: filteredItems.length});
              continue;
            }

            if (printer.ip) {
              for (let i = 0; i < (event.copy || 1); i++) {
                await sendPrinterAndroid(
                  getBigTerminalData(
                    {
                      ...order,
                      items: filteredItems
                    },
                    JSON.parse(restaurantData),
                    userInfo
                  ),
                  {
                    ip: printer.ip,
                    port: Number(printer.port),
                    timeout: 400,
                    autoCut: true,
                    mmFeedPaper: 230,
                    printerDpi: 203,
                    printerWidthMM: 80,
                    printerNbrCharactersPerLine: 42,
                  }
                );
                // Add a small delay between prints
                await new Promise(resolve => setTimeout(resolve, 50));
              }
            } else if (printer.address || printer.macAddress) {
              for (let i = 0; i < (event.copy || 1); i++) {
                await sendPrinterAndroid(
                  getBigTerminalData(
                    {
                      ...order,
                      items: filteredItems
                    },
                    JSON.parse(restaurantData),
                    userInfo
                  ),
                  {
                    macAddress: (printer.address || printer.macAddress),
                  }
                );
                // Add a small delay between prints
                await new Promise(resolve => setTimeout(resolve, 50));
              }
            }
          }
        }
      }
    }

    await AsyncStorage.setItem('kitchenPrinted', 'true');

    // Hide the notification when printing is complete
    Toast.hide();

    return BackgroundFetch.BackgroundFetchResult.NewData;
  } catch (error) {
    console.error("Background kitchen printing task error:", error);

    // Hide the notification and show error if printing fails
    Toast.hide();
    Toast.show({
      type: 'error',
      position: 'bottom',
      text1: 'Kitchen printing failed',
      text2: error.message,
      visibilityTime: 3000,
    });

    return BackgroundFetch.BackgroundFetchResult.Failed;
  }
});

// Trigger kitchen printer task
export const triggerKitchenPrinting = async (order, items) => {
  try {
    await AsyncStorage.setItem('kitchenPrinted', 'false');

    const { Platform } = require('react-native');
    if (Platform.OS !== 'android') return false;

    try {
      // Execute the task with the data
      await TaskManager.executeTaskAsync(PRINT_KITCHEN_TASK, { order, items });
      return true;
    } catch (taskError) {
      console.error('Failed to execute kitchen printing task:', taskError);

      // Fall back to direct implementation
      const userInfo = await getUserInfo();
      const restaurantData = await AsyncStorage.getItem("restaurant");

      const { sendPrinterAndroid } = require('./printerLibrary/printer');
      const { getBigTerminalData } = require('./printerLibrary/oldPrinter');

      const storedPrinterEvents = await AsyncStorage.getItem('printerEvents');
      const printerEvents = storedPrinterEvents ? JSON.parse(storedPrinterEvents) : [];

      if (printerEvents.length > 0) {
        const terminalEvents = printerEvents.filter(event => event.type === "terminal");

        for (let eventIndex = 0; eventIndex < terminalEvents.length; eventIndex++) {
          const event = terminalEvents[eventIndex];

          if (event.printers && event.printers.length > 0) {
            for (let printerIndex = 0; printerIndex < event.printers.length; printerIndex++) {
              const printer = event.printers[printerIndex];

              // Filter items based on event.products
              const filteredItems = items.filter(item =>
                event?.products?.includes(item.itemId || item.item._id || item.item)
              );

              if (!filteredItems || filteredItems.length === 0) {
                console.log("No items for this printer event", {name: event.name, products: filteredItems.length});
                continue;
              }

              if (printer.ip) {
                for (let i = 0; i < (event.copy || 1); i++) {
                  await sendPrinterAndroid(
                    getBigTerminalData(
                      {
                        ...order,
                        items: filteredItems
                      },
                      JSON.parse(restaurantData),
                      userInfo
                    ),
                    {
                      ip: printer.ip,
                      port: Number(printer.port),
                      timeout: 400,
                      autoCut: true,
                      mmFeedPaper: 230,
                      printerDpi: 203,
                      printerWidthMM: 80,
                      printerNbrCharactersPerLine: 42,
                    }
                  );
                  await new Promise(resolve => setTimeout(resolve, 50));
                }
              } else if (printer.address || printer.macAddress) {
                for (let i = 0; i < (event.copy || 1); i++) {
                  await sendPrinterAndroid(
                    getBigTerminalData(
                      {
                        ...order,
                        items: filteredItems
                      },
                      JSON.parse(restaurantData),
                      userInfo
                    ),
                    {
                      macAddress: (printer.address || printer.macAddress),
                    }
                  );
                  await new Promise(resolve => setTimeout(resolve, 50));
                }
              }
            }
          }
        }
      }

      await AsyncStorage.setItem('kitchenPrinted', 'true');
      return true;
    }
  } catch (error) {
    console.error('Failed to trigger kitchen printing:', error);
    return false;
  }
};

// Check if kitchen has been printed
export const checkKitchenPrinted = async () => {
  const printed = await AsyncStorage.getItem('kitchenPrinted');
  return printed === 'true';
};

// Define fiscal printer task
TaskManager.defineTask(FISCAL_PRINTER_TASK, async ({ data }) => {
  try {
    const { orderData, operation } = data;

    if (!orderData || !operation) {
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    // Import the FiscatPrinter module
    const { FiscatPrinter } = require('../paymentModules/FiscatFiscalModule');

    let result = false;

    // Execute the requested operation
    switch (operation) {
      case 'printReceipt':
        await FiscatPrinter.printReceipt(orderData);
        result = true;
        break;
      case 'printDeliveryInfo':
        await FiscatPrinter.printDeliveryInfo(orderData);
        result = true;
        break;
      case 'openDay':
        await FiscatPrinter.openDay();
        result = true;
        break;
      case 'closeDay':
        await FiscatPrinter.closeDay();
        result = true;
        break;
      default:
        console.error('Unknown fiscal printer operation:', operation);
        return BackgroundFetch.BackgroundFetchResult.Failed;
    }

    await AsyncStorage.setItem('fiscalPrinterOperationCompleted', 'true');
    return BackgroundFetch.BackgroundFetchResult.NewData;
  } catch (error) {
    console.error("Background fiscal printer task error:", error);
    return BackgroundFetch.BackgroundFetchResult.Failed;
  }
});

// Trigger fiscal printer task
export const triggerFiscalPrinter = async (orderData, operation) => {
  try {
    await AsyncStorage.setItem('fiscalPrinterOperationCompleted', 'false');

    try {
      // Execute the task with the data
      await TaskManager.executeTaskAsync(FISCAL_PRINTER_TASK, { orderData, operation });
      return true;
    } catch (taskError) {
      console.error('Failed to execute fiscal printer task:', taskError);

      // Fall back to direct implementation
      const { FiscatPrinter } = require('../paymentModules/FiscatFiscalModule');

      let result = false;

      // Execute the requested operation directly
      switch (operation) {
        case 'printReceipt':
          await FiscatPrinter.printReceipt(orderData);
          result = true;
          break;
        case 'printDeliveryInfo':
          await FiscatPrinter.printDeliveryInfo(orderData);
          result = true;
          break;
        case 'openDay':
          await FiscatPrinter.openDay();
          result = true;
          break;
        case 'closeDay':
          await FiscatPrinter.closeDay();
          result = true;
          break;
        default:
          console.error('Unknown fiscal printer operation:', operation);
          return false;
      }

      await AsyncStorage.setItem('fiscalPrinterOperationCompleted', 'true');
      return result;
    }
  } catch (error) {
    console.error('Failed to trigger fiscal printer:', error);
    return false;
  }
};

// Check if fiscal printer operation has been completed
export const checkFiscalPrinterOperationCompleted = async () => {
  const completed = await AsyncStorage.getItem('fiscalPrinterOperationCompleted');
  return completed === 'true';
};
